export type NativeTokenStore = {
  get(): Promise<string | null>;
  set(token: string): Promise<void>;
  remove(): Promise<void>;
};

// Serialize disk writes and invalidate responses from requests made before logout.
export function createNativeSession(store: NativeTokenStore) {
  let token: string | null = null;
  let generation = 0;
  let writes: Promise<void> = Promise.resolve();
  const enqueue = (operation: () => Promise<void>) => {
    const result = writes.then(operation);
    writes = result.catch(() => undefined);
    return result;
  };
  return {
    get token() { return token; },
    get generation() { return generation; },
    async restore() { token = await store.get(); },
    async accept(value: string, expectedGeneration: number) {
      if (generation !== expectedGeneration) return;
      await enqueue(async () => {
        if (generation !== expectedGeneration) return;
        await store.set(value);
        if (generation === expectedGeneration) { token = value; generation++; }
      });
    },
    clear() {
      generation++;
      token = null;
      return enqueue(() => store.remove());
    },
  };
}

const tokenIssuingPaths = new Set(["/api/auth/local/login", "/api/auth/staging/login", "/api/auth/session/exchange", "/api/dev/login-as"]);

export function createNativeFetch(baseFetch: typeof fetch, apiOrigin: string, session: ReturnType<typeof createNativeSession>, onExpired: () => void): typeof fetch {
  return async (input, init) => {
    const resolvedInput = typeof input === "string" ? new URL(input, typeof window === "undefined" ? apiOrigin : window.location.href) : input;
    const request = new Request(resolvedInput, init);
    const url = new URL(request.url);
    if (url.origin !== apiOrigin || !url.pathname.startsWith("/api/")) return baseFetch(request);
    const generation = session.generation;
    const token = session.token;
    const headers = new Headers(request.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const logout = url.pathname === "/api/auth/logout";
    try {
      const response = await baseFetch(new Request(request, { headers, credentials: "omit", redirect: "error" }));
      if (response.status === 401 && token && session.generation === generation && session.token === token && !tokenIssuingPaths.has(url.pathname)) {
        await session.clear();
        onExpired();
      } else if (response.ok && tokenIssuingPaths.has(url.pathname)) {
        const body = await response.clone().json() as { sessionToken?: unknown };
        if (typeof body.sessionToken !== "string" || !/^[A-Za-z0-9_-]{20,256}$/.test(body.sessionToken)) {
          throw new Error("Kyçja në aplikacion nuk u konfirmua. Provo përsëri.");
        }
        await session.accept(body.sessionToken, generation);
      }
      return response;
    } finally {
      if (logout) await session.clear();
    }
  };
}
